import {
    ActivitiesGqlResponseType,
    ActivityGqlOperation,
    ActivityGqlQueryArgs,
    ActivityGqlQueryArgsType,
    GqlBodyProps,
    GqlQueryAndVariablesBuilder,
    GqlResponseError,
    ActivitiesGqlRequestPayloadValidationStatus,
    ActivityGqlConfigurationValueType,
} from 'store/api/api.types';
import { detailsLoadFinished } from 'component/page/detail/store/details';
import { searchLoadFinished } from 'component/page/search/store/search';
import { availabilityLoadFinished } from 'component/page/detail/store/availabilityIndex';
import { InternalContext } from 'gql/autogen/InternalContext';
import { ExperimentId } from 'core/type/ExperimentId';
import { RootState } from 'store/store.types';
import { EmptyResponseValues } from 'store/api/ApiConstants';
import { logInfo } from 'core-framework/util/LogMessage';
import { calendarLoadFinished } from 'component/page/detail/store/calendar';
import { reviewLoadFinished } from 'component/page/detail/store/review';
import { ForcedExperiment } from 'gql/autogen/ForcedExperiment';
import { getStoreState } from 'store/store';
import searchQueryAndVariablesBuilder, { validateSearchRequest } from 'gql/activityBuilder/search';
import detailsQueryAndVariablesBuilder from 'gql/activityBuilder/details';
import calendarQueryAndVariablesBuilder from 'gql/activityBuilder/calendar';
import availabilityQueryAndVariablesBuilder from 'gql/activityBuilder/availability';
import reviewQueryAndVariablesBuilder from 'gql/activityBuilder/review';

const getDataOrDefault = (operation: ActivityGqlOperation, response?: ActivitiesGqlResponseType) => {
    if (!response || !response.data) {
        return EmptyResponseValues[operation];
    }

    const data = response.data as unknown as Record<string, unknown>;
    return (data && data[operation]) || EmptyResponseValues[operation];
};

export const buildInternalContext = (state: RootState): InternalContext => {
    const config = state.bootstrap.config;
    const userVariant = config.userVariant;

    const forcedExperiments = Object.entries(config.experiments).reduce<ForcedExperiment[]>((apiExp, [expName, isBVariant]) => {
        if (expName !== ExperimentId.ACT_TEST.toString()) {
            apiExp.push({ experiment: expName, variant: isBVariant ? 'B' : 'A' });
        }
        return apiExp;
    }, []);

    const context: InternalContext = {
        currency: config.currency.code,
        experimentInfo: {
            forcedExperiments,
        },
    };
    if (userVariant === 'B' || userVariant === 'A') {
        context.experimentInfo.forceUserVariant = userVariant;
    }
    if (config.isLoggedIn) {
        context.memberId = config.memberId;
        if (config.partner.loyaltyPartnerClaim) {
            context.loyaltyPartnerClaim = config.partner.loyaltyPartnerClaim;
        }
    }
    return context;
};

const buildQueryAndVariables = (args: ActivityGqlQueryArgs<ActivityGqlQueryArgsType>, gqlQueryAndVariablesBuilder: GqlQueryAndVariablesBuilder): GqlBodyProps => {
    const state = args.state || getStoreState();
    const context = buildInternalContext(state);
    const gqlBody = gqlQueryAndVariablesBuilder(state, args.props, context);
    const logTags = gqlBody.logTags;

    logInfo(`gql_request ${logTags.operation}`, gqlBody.pageTypeId, logTags, { request: JSON.stringify(gqlBody.variables) });
    return gqlBody;
};

const validateRequest = (
    payload: GqlBodyProps,
    operation: ActivityGqlOperation,
    validatorFunction: (request: Record<string, unknown>) => Required<GqlResponseError> | undefined,
): ActivitiesGqlRequestPayloadValidationStatus | undefined => {
    if (!payload || !payload.variables) return undefined;
    const res = validatorFunction(payload.variables);
    if (res) {
        return {
            data: { [operation]: { isSuccess: false } },
            error: {
                ...payload,
                operation,
                data: res,
                status: res.status,
            },
        };
    }
    return undefined;
};

export const ActivityGqlConfiguration: ActivityGqlConfigurationValueType = {
    search: {
        operation: ActivityGqlOperation.search,
        buildRequestPayload: (args) => buildQueryAndVariables(args, searchQueryAndVariablesBuilder),
        handleResponse: (response) => getDataOrDefault(ActivityGqlOperation.search, response),
        setResponseInStateFn: () => searchLoadFinished,
        validateRequestPayload: (payload: GqlBodyProps) => validateRequest(payload, ActivityGqlOperation.search, validateSearchRequest),
    },
    details: {
        operation: ActivityGqlOperation.details,
        buildRequestPayload: (args) => buildQueryAndVariables(args, detailsQueryAndVariablesBuilder),
        handleResponse: (response) => getDataOrDefault(ActivityGqlOperation.details, response),
        setResponseInStateFn: () => detailsLoadFinished,
        validateRequestPayload: (_payload: GqlBodyProps) => undefined,
    },
    availability: {
        operation: ActivityGqlOperation.availability,
        buildRequestPayload: (args) => buildQueryAndVariables(args, availabilityQueryAndVariablesBuilder),
        handleResponse: (response) => getDataOrDefault(ActivityGqlOperation.availability, response),
        setResponseInStateFn: () => availabilityLoadFinished,
        validateRequestPayload: (_payload: GqlBodyProps) => undefined,
    },
    calendar: {
        operation: ActivityGqlOperation.calendar,
        buildRequestPayload: (args) => buildQueryAndVariables(args, calendarQueryAndVariablesBuilder),
        handleResponse: (response) => getDataOrDefault(ActivityGqlOperation.calendar, response),
        setResponseInStateFn: () => calendarLoadFinished,
        validateRequestPayload: (_payload: GqlBodyProps) => undefined,
    },
    review: {
        operation: ActivityGqlOperation.review,
        buildRequestPayload: (args) => buildQueryAndVariables(args, reviewQueryAndVariablesBuilder),
        handleResponse: (response) => getDataOrDefault(ActivityGqlOperation.review, response),
        setResponseInStateFn: () => reviewLoadFinished,
        validateRequestPayload: (_payload: GqlBodyProps) => undefined,
    },
};
